import { useMemo } from 'react'
import { pnlMatrix } from '@apna/shared'
import { cn } from '@/lib/utils'
import { compactInr, inr } from './quantity'
const cell = (v: number) => (v > 0 ? '+' : v < 0 ? '-' : '') + compactInr(Math.abs(v))

export function PayoffHeatGrid({
  surface,
  rangePct,
  asPercent,
  risk,
  spot,
}: {
  surface: Parameters<typeof pnlMatrix>[0]
  rangePct: number
  asPercent: boolean
  risk: number | null
  spot: number
}) {
  const matrix = useMemo(() => pnlMatrix(surface, { rangePct }), [surface, rangePct])
  const base = asPercent && risk !== null && risk > 0 ? risk : matrix.scale

  return (
    <table className="w-full border-separate border-spacing-0" data-testid="payoff-table">
      <thead>
        <tr>
          <th className="sticky left-0 top-0 z-20 bg-card px-2 py-1.5 text-left text-[9.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Spot
          </th>
          {matrix.columns.map((c) => (
            <th
              key={c.label}
              className="sticky top-0 z-10 bg-card px-2 py-1.5 text-right text-[9.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="num">
        {matrix.rows.map((row) => {
          // The row closest to the live spot, marked rather than guessed
          // at from the middle index.
          const isSpot = Math.abs(row.spot - spot) < (spot * rangePct) / 100 / (matrix.rows.length - 1)
          return (
            <tr key={row.spot}>
              <th
                scope="row"
                className={cn(
                  'sticky left-0 z-10 whitespace-nowrap border-t border-border bg-card px-2 py-[5px] text-left text-[11px] font-semibold',
                  isSpot && 'text-primary',
                )}
              >
                {Math.round(row.spot).toLocaleString('en-IN')}
                <span
                  className={cn(
                    'ml-1.5 text-[9.5px] font-normal',
                    row.fromSpot >= 0 ? 'text-success/70' : 'text-destructive/70',
                  )}
                >
                  {row.fromSpot >= 0 ? '+' : ''}
                  {row.fromSpot.toFixed(1)}%
                </span>
              </th>
              {row.cells.map((value, j) => {
                const weight = Math.min(Math.abs(value) / Math.max(base, 1), 1)
                return (
                  <td
                    key={j}
                    title={inr(Math.round(value))}
                    className={cn(
                      'border-t border-border px-2 py-[5px] text-right text-[11.5px] font-semibold tabular-nums',
                      value >= 0 ? 'text-success' : 'text-destructive',
                    )}
                    style={{
                      // A wash, not a fill: the number stays the content.
                      background: `color-mix(in srgb, ${
                        value >= 0 ? 'hsl(var(--success))' : 'hsl(var(--destructive))'
                      } ${(8 + weight * 22).toFixed(0)}%, transparent)`,
                    }}
                  >
                    {asPercent
                      ? `${value >= 0 ? '+' : '-'}${((Math.abs(value) / Math.max(base, 1)) * 100).toFixed(0)}%`
                      : cell(value)}
                  </td>
                )
              })}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
